import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Card, Field, FormGrid } from '@crave/ui';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { HistoryLink } from '../components/HistoryLink.tsx';
import { Notice, Section, errorText, today } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

interface Slot {
  id: string;
  label: string;
  capacity: number;
  reserved_units: number;
  is_blocked: boolean;
  note: string | null;
  orders: { order_number: string; status: string; units: number }[] | null;
}
interface SpecialDay {
  id: string;
  service_date: string;
  is_closed: boolean;
  opens_at: string | null;
  closes_at: string | null;
  slot_capacity: number | null;
  note: string | null;
  booked_units: number;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** "2026-12-25" -> "Fri, Dec 25, 2026", read as a calendar date (no time zone shift). */
function dayLabel(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, day ?? 1)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "18:00" or "18:00:00" -> "6:00 PM". */
function timeLabel(t: string | null): string {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const hh = (((h ?? 0) + 11) % 12) + 1;
  return `${hh}:${String(m ?? 0).padStart(2, '0')} ${(h ?? 0) < 12 ? 'AM' : 'PM'}`;
}

const pizzas = (n: number) => `${n} ${n === 1 ? 'pizza' : 'pizzas'}`;

interface Grid {
  date: string;
  physical_max_per_slot: number;
  override: {
    id: string;
    isClosed: boolean;
    opensAt: string | null;
    closesAt: string | null;
    slotCapacity: number | null;
    note: string | null;
  } | null;
  slots: Slot[];
}

const SLOT_HEAD = ['Window', 'Booked', 'Capacity', 'Blocked', 'Orders'];

/* Controls match the shared form fields (44px, 2px border). In a narrow space each window becomes
   a stacked, labelled block instead of a squeezed table row. */
const CAPACITY_CSS = `
.cap-control { height: var(--touch); padding: 0 10px; border: 2px solid var(--line); border-radius: var(--radius-sm); background: var(--paper); color: var(--ink); }
.cap-slots { container-type: inline-size; min-width: 0; }
.cap-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.cap-table th { text-align: left; padding: 8px 10px; border-bottom: 2px solid var(--line); color: var(--ink-soft); font-weight: 600; white-space: nowrap; }
.cap-table td { padding: 8px 10px; border-bottom: 1px solid var(--line); vertical-align: top; }
.cap-table td.cap-empty { padding: 16px; color: var(--ink-soft); }
.cap-val { min-height: var(--touch); display: flex; align-items: center; }
.cap-label { display: none; }
.cap-check { width: 18px; height: 18px; margin: 0; }
.cap-check-hit { min-width: var(--touch); cursor: pointer; }
.cap-link { background: none; border: 0; padding: 0; margin-left: 8px; min-height: 32px; color: var(--crave-red); font: inherit; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.cap-row-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; }
.cap-notice-actions { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; margin-left: 8px; vertical-align: middle; }
.cap-notice-actions .cap-link { margin-left: 0; }
.cap-sr { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.cap-days { container-type: inline-size; min-width: 0; }
.cap-days-table td { white-space: nowrap; }
.cap-days-table td.cap-note { white-space: normal; min-width: 180px; max-width: 360px; }
.cap-daylink { background: none; border: 0; padding: 0; min-height: 32px; color: var(--ink); font: inherit; text-align: left; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.cap-day-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
@media (max-width: 560px) {
  .cap-day-actions > button { flex: 1 1 100%; }
}
@container (max-width: 560px) {
  .cap-table thead { display: none; }
  .cap-table, .cap-table tbody { display: block; }
  .cap-table tr { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr); column-gap: 12px; row-gap: 4px; padding: 12px 0; border-bottom: 1px solid var(--line); }
  .cap-table td { display: block; padding: 0; border: 0; min-width: 0; }
  .cap-table td.cap-window, .cap-table td.cap-orders, .cap-table td.cap-empty { grid-column: 1 / -1; }
  .cap-table td.cap-window .cap-val { min-height: 0; font-weight: 600; }
  .cap-table td.cap-orders .cap-val { min-height: 0; }
  .cap-table td.cap-orders.cap-none { display: none; }
  .cap-label { display: block; font-size: 12px; line-height: 1.3; color: var(--ink-soft); }
}
/* Upcoming special days stack whenever the full table would not fit (tablet and phone), so Remove
   and History never scroll out of sight: the date, then hours, pizzas and booked, the note, actions. */
@container (max-width: 820px) {
  .cap-days-table thead { display: none; }
  .cap-days-table, .cap-days-table tbody { display: block; }
  .cap-days-table tr { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); column-gap: 12px; row-gap: 10px; padding: 14px 0 6px; border-bottom: 1px solid var(--line); }
  .cap-days-table td, .cap-days-table td.cap-note { display: block; padding: 0; border: 0; min-width: 0; max-width: none; white-space: normal; }
  .cap-days-table td.cap-window, .cap-days-table td.cap-note, .cap-days-table td.cap-day-row-actions, .cap-days-table td.cap-empty { grid-column: 1 / -1; }
  .cap-days-table td.cap-note { order: 1; }
  .cap-days-table td.cap-day-row-actions { order: 2; }
  .cap-days-table td.cap-note.cap-none { display: none; }
  .cap-days-table td .cap-val { min-height: 0; }
  .cap-days-table td.cap-window .cap-val { font-weight: 600; }
  .cap-days-table td.cap-day-row-actions .cap-val { min-height: var(--touch); }
  .cap-days-table td.cap-day-row-actions button { margin-inline-start: -12px; }
  .cap-days .cap-label { display: block; font-size: 12px; line-height: 1.3; color: var(--ink-soft); }
}
@container (max-width: 420px) {
  .cap-days-table tr { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
}
`;

const DAYS_HEAD = ['Date', 'Hours', 'Pizzas per window', 'Note', 'Booked'];

export function Capacity() {
  const qc = useQueryClient();
  const staff = useStaff();
  // Setting or closing a day needs schedule.manage; removing one also works with records.delete.
  const canSchedule = staff.can('schedule.manage');
  const canRemove = canSchedule || staff.can('records.delete');
  // The location's calendar day, so the page matches the Dashboard's "Today" in the evening.
  const [date, setDate] = useState<string>(today);
  const q = useQuery({
    queryKey: ['slots', staff.location.slug, date],
    queryFn: async () =>
      (await api.get<Grid>(`/admin/capacity/slots?location=${staff.location.slug}&date=${date}`))
        .data,
  });
  const locations = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () =>
      (await api.get<{ items: { id: string }[] }>('/admin/locations')).data.items,
  });
  const locationId = staff.location.id || locations.data?.[0]?.id;
  const [msg, setMsg] = useState<string | null>(null);
  // Bumped after a rejected edit so every capacity box remounts with the saved value.
  const [rev, setRev] = useState(0);
  const fail = (e: unknown) => setMsg(errorText(e));
  // Special-day form errors show inside that card, next to its buttons, not at the top of the page.
  const [dayMsg, setDayMsg] = useState<string | null>(null);
  const specialDays = useQuery({
    queryKey: ['special-days', staff.location.slug],
    queryFn: async () =>
      (
        await api.get<{ items: SpecialDay[] }>(
          `/admin/capacity/overrides?location=${staff.location.slug}`,
        )
      ).data.items,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['slots'] });
    void qc.invalidateQueries({ queryKey: ['special-days'] });
  };
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/admin/capacity/slots/${v.id}`, v.body),
    onSuccess: () => {
      setMsg(null);
      refresh();
    },
    onError: (e) => {
      fail(e);
      setRev((r) => r + 1);
      refresh();
    },
  });
  // Errors are shown by the caller: on the page for special hours, inside the dialog for closing.
  const override = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put('/admin/capacity/overrides', body),
    onSuccess: (_r, body) => {
      setMsg(null);
      setDayMsg(null);
      // A note belongs to the day it was saved with; don't carry it to the next one.
      setOv((o) => ({ ...o, slot_capacity: '', note: '' }));
      const day = String(body['service_date']);
      setSaved(
        body['is_closed']
          ? `${dayLabel(day)} is marked closed. Customers can't pick that day.`
          : `Special hours saved for ${dayLabel(day)}.`,
      );
      setDate(day);
      refresh();
    },
  });
  // Errors show inside the Remove dialog.
  const clear = useMutation({
    mutationFn: (v: { locId: string; day: string; reason: string | undefined }) =>
      api.delete(
        `/admin/capacity/overrides/${v.locId}/${v.day}${v.reason ? `?reason=${encodeURIComponent(v.reason)}` : ''}`,
      ),
    onSuccess: (_r, v) => {
      setMsg(null);
      setSaved(`Removed the special day on ${dayLabel(v.day)}. Regular hours apply again.`);
      refresh();
    },
  });
  const noLocation = (show: (text: string) => void = setMsg) =>
    show(
      locations.isError
        ? `Could not load your location. ${errorText(locations.error, '')}`
        : 'Still loading your location. Try again in a moment.',
    );
  const [ov, setOv] = useState({
    opens_at: '11:00',
    closes_at: '18:00',
    slot_capacity: '',
    note: '',
  });
  // The special day being set. Follows the date picked at the top until changed here.
  const [ovDate, setOvDate] = useState<string>(date);
  const [saved, setSaved] = useState<string | null>(null);
  // The day waiting for "Close this day" or "Remove" to be confirmed.
  const [closing, setClosing] = useState<{ day: string; note: string } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const slots = q.data?.slots ?? [];

  /** Pizzas already booked on a day, when the page knows. */
  const bookedOn = (day: string): number | null => {
    const listed = specialDays.data?.find((d) => d.service_date === day);
    if (listed) return listed.booked_units;
    if (q.data && q.data.date === day)
      return q.data.slots.reduce((n, s) => n + s.reserved_units, 0);
    return null;
  };
  const closingBooked = closing ? bookedOn(closing.day) : null;
  const removingDay = removing
    ? specialDays.data?.find((d) => d.service_date === removing)
    : undefined;
  const removingOverride =
    removing && q.data?.date === removing && q.data.override ? q.data.override : null;
  const removingSetTo = removingDay
    ? removingDay.is_closed
      ? 'closed'
      : `open ${timeLabel(removingDay.opens_at)} to ${timeLabel(removingDay.closes_at)}`
    : removingOverride
      ? removingOverride.isClosed
        ? 'closed'
        : `open ${timeLabel(removingOverride.opensAt)} to ${timeLabel(removingOverride.closesAt)}`
      : null;

  return (
    <Section
      title="Capacity"
      right={
        <input
          type="date"
          value={date}
          onChange={(e) => {
            setMsg(null);
            setDayMsg(null);
            setSaved(null);
            const next = e.target.value || today();
            setDate(next);
            setOvDate(next);
          }}
          className="cap-control"
          aria-label="Date"
        />
      }
    >
      <style>{CAPACITY_CSS}</style>
      {msg && <Notice kind="error">{msg}</Notice>}
      {q.isError && <Notice kind="error">Could not load windows. {errorText(q.error, '')}</Notice>}
      {q.data && q.data.slots.length === 0 && (
        <Notice>
          No service on this day. Turn the weekday on under Menu, prices & hours, or add a special
          day below.
        </Notice>
      )}
      {q.data && (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
          The oven can physically do about {q.data.physical_max_per_slot} pizzas per window. Lower a
          window's capacity or block it for breaks; blocked windows are invisible to customers.
        </p>
      )}
      {q.data?.override && (
        <Notice>
          {q.data.override.isClosed
            ? 'This day is marked closed.'
            : `Special hours on this day: ${timeLabel(q.data.override.opensAt)} to ${timeLabel(q.data.override.closesAt)}.`}
          {q.data.override.note ? ` Note: ${q.data.override.note}` : ''}
          <span className="cap-notice-actions">
            {canRemove && (
              <button
                type="button"
                className="cap-link"
                onClick={() => (locationId ? setRemoving(q.data.date) : noLocation())}
                disabled={clear.isPending}
              >
                Remove
              </button>
            )}
            <HistoryLink
              entityType="service_date_overrides"
              entityId={q.data.override.id}
              name={`the special day on ${dayLabel(q.data.date)}`}
            />
          </span>
        </Notice>
      )}
      <div className="cap-slots">
        <div style={{ overflowX: 'auto' }}>
          <table className="cap-table">
            <thead>
              <tr>
                {SLOT_HEAD.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.length === 0 && (
                <tr>
                  <td colSpan={SLOT_HEAD.length} className="cap-empty">
                    {q.isLoading ? 'Loading…' : 'No windows.'}
                  </td>
                </tr>
              )}
              {slots.map((s) => {
                const orders = (s.orders ?? [])
                  .map((o) => `${o.order_number} (${o.units})`)
                  .join(', ');
                return (
                  <tr key={s.id}>
                    <td className="cap-window">
                      <div className="cap-val">{s.label}</div>
                    </td>
                    <td>
                      <span className="cap-label">{SLOT_HEAD[1]}</span>
                      <div className="cap-val">{`${s.reserved_units} / ${s.capacity}`}</div>
                    </td>
                    <td>
                      <span className="cap-label">{SLOT_HEAD[2]}</span>
                      <div className="cap-val">
                        <input
                          // Keyed by slot, saved capacity and revision: a new date, a refetch or a
                          // rejected edit remounts the box, so it never shows (or re-sends) a stale
                          // typed value.
                          key={`${s.id}:${s.capacity}:${rev}`}
                          type="number"
                          min={s.reserved_units}
                          max={100}
                          step={1}
                          defaultValue={s.capacity}
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            const n = Number(raw);
                            if (raw !== '' && n === s.capacity) return;
                            if (raw === '' || !Number.isInteger(n) || n < 0 || n > 100) {
                              setMsg('Capacity must be a whole number from 0 to 100.');
                              e.target.value = String(s.capacity);
                              return;
                            }
                            if (n < s.reserved_units) {
                              setMsg(
                                `${s.label} already has ${s.reserved_units} booked, so capacity can't go below that.`,
                              );
                              e.target.value = String(s.capacity);
                              return;
                            }
                            patch.mutate({ id: s.id, body: { capacity: n } });
                          }}
                          className="cap-control"
                          style={{ width: 88 }}
                          aria-label={`Capacity ${s.label}`}
                        />
                      </div>
                    </td>
                    <td>
                      <span className="cap-label">{SLOT_HEAD[3]}</span>
                      <label className="cap-val cap-check-hit">
                        <input
                          key={`${s.id}:b`}
                          type="checkbox"
                          className="cap-check"
                          checked={s.is_blocked}
                          onChange={(e) =>
                            patch.mutate({ id: s.id, body: { is_blocked: e.target.checked } })
                          }
                          aria-label={`Block ${s.label}`}
                        />
                      </label>
                    </td>
                    <td className={orders ? 'cap-orders' : 'cap-orders cap-none'}>
                      <span className="cap-label">{SLOT_HEAD[4]}</span>
                      <div className="cap-val">{orders}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* min-width 0: the upcoming list scrolls inside the card instead of widening the page. */}
      <Card style={{ minWidth: 0 }}>
        <strong style={{ display: 'block' }}>Special days: holidays, events and closures</strong>
        <p style={{ margin: '4px 0 12px', fontSize: 14, color: 'var(--ink-soft)' }}>
          Pick a date, then set different hours for it or close it. This replaces your regular
          weekly hours for that one date only. Orders already booked for that day are kept.
        </p>
        {saved && (
          <div style={{ marginBottom: 12 }}>
            <Notice kind="ok">{saved}</Notice>
          </div>
        )}
        {canSchedule ? (
          <>
            <FormGrid min={200}>
              <Field
                label="Date"
                type="date"
                value={ovDate}
                min={today()}
                onChange={(e) => {
                  setSaved(null);
                  setDayMsg(null);
                  setOvDate(e.target.value);
                }}
              />
              <Field
                label="Opens"
                type="time"
                value={ov.opens_at}
                onChange={(e) => setOv({ ...ov, opens_at: e.target.value })}
              />
              <Field
                label="Closes"
                type="time"
                value={ov.closes_at}
                onChange={(e) => setOv({ ...ov, closes_at: e.target.value })}
              />
              <Field
                label="Pizzas per window"
                inputMode="numeric"
                value={ov.slot_capacity}
                onChange={(e) => setOv({ ...ov, slot_capacity: e.target.value })}
                placeholder="Usual amount"
              />
              <Field
                label="Note (optional)"
                wide
                maxLength={200}
                value={ov.note}
                onChange={(e) => setOv({ ...ov, note: e.target.value })}
                placeholder="e.g. Fourth of July market, Thanksgiving"
              />
            </FormGrid>
            {dayMsg && (
              <div role="alert" style={{ margin: '12px 0' }}>
                <Notice kind="error">{dayMsg}</Notice>
              </div>
            )}
            <div className="cap-day-actions">
              <Button
                variant="secondary"
                disabled={override.isPending}
                onClick={() => {
                  setSaved(null);
                  if (!locationId) return noLocation(setDayMsg);
                  if (!DATE.test(ovDate)) return setDayMsg('Choose a date first.');
                  if (!ov.opens_at || !ov.closes_at || ov.closes_at <= ov.opens_at)
                    return setDayMsg('Closing time must be after opening time.');
                  const cap = ov.slot_capacity.trim();
                  const n = Number(cap);
                  if (cap !== '' && (!Number.isInteger(n) || n < 0 || n > 100))
                    return setDayMsg('Pizzas per window must be a whole number from 0 to 100.');
                  setDayMsg(null);
                  override.mutate(
                    {
                      location_id: locationId,
                      service_date: ovDate,
                      is_closed: false,
                      opens_at: ov.opens_at,
                      closes_at: ov.closes_at,
                      slot_capacity: cap ? n : null,
                      ...(ov.note.trim() ? { note: ov.note.trim() } : {}),
                    },
                    { onError: (e) => setDayMsg(errorText(e)) },
                  );
                }}
              >
                {DATE.test(ovDate)
                  ? `Set special hours for ${dayLabel(ovDate)}`
                  : 'Set special hours'}
              </Button>
              <Button
                variant="danger"
                disabled={override.isPending}
                onClick={() => {
                  if (!locationId) return noLocation(setDayMsg);
                  if (!DATE.test(ovDate)) return setDayMsg('Choose a date first.');
                  setDayMsg(null);
                  setClosing({ day: ovDate, note: ov.note.trim() });
                }}
              >
                {DATE.test(ovDate) ? `Close ${dayLabel(ovDate)}` : 'Close this day'}
              </Button>
            </div>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
            Only the owner or a manager can set or close special days.
          </p>
        )}
        <strong style={{ display: 'block', margin: '24px 0 8px' }}>Upcoming special days</strong>
        {specialDays.isError && (
          <Notice kind="error">
            Could not load special days. {errorText(specialDays.error, '')}
          </Notice>
        )}
        {/* Its own table: on a phone each special day stacks, so Remove and History stay in view. */}
        <div className="cap-days">
          <div style={{ overflowX: 'auto' }}>
            <table className="cap-table cap-days-table">
              <thead>
                <tr>
                  {DAYS_HEAD.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                  <th>
                    <span className="cap-sr">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(specialDays.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={DAYS_HEAD.length + 1} className="cap-empty">
                      {specialDays.isLoading ? 'Loading…' : 'No special days coming up.'}
                    </td>
                  </tr>
                )}
                {(specialDays.data ?? []).map((d) => (
                  <tr key={d.service_date}>
                    <td className="cap-window">
                      <div className="cap-val">
                        <button
                          type="button"
                          className="cap-daylink"
                          aria-label={`Show ${dayLabel(d.service_date)} above`}
                          onClick={() => {
                            setSaved(null);
                            setDate(d.service_date);
                            setOvDate(d.service_date);
                          }}
                        >
                          {dayLabel(d.service_date)}
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className="cap-label">{DAYS_HEAD[1]}</span>
                      <div className="cap-val">
                        {d.is_closed
                          ? 'Closed'
                          : `${timeLabel(d.opens_at)} to ${timeLabel(d.closes_at)}`}
                      </div>
                    </td>
                    <td>
                      <span className="cap-label">{DAYS_HEAD[2]}</span>
                      <div className="cap-val">
                        {d.is_closed
                          ? 'None'
                          : d.slot_capacity === null
                            ? 'Usual'
                            : String(d.slot_capacity)}
                      </div>
                    </td>
                    <td className={d.note ? 'cap-note' : 'cap-note cap-none'}>
                      <span className="cap-label">{DAYS_HEAD[3]}</span>
                      <div className="cap-val">{d.note ?? ''}</div>
                    </td>
                    <td>
                      <span className="cap-label">{DAYS_HEAD[4]}</span>
                      <div className="cap-val">
                        {d.booked_units ? pizzas(d.booked_units) : 'None'}
                      </div>
                    </td>
                    <td className="cap-day-row-actions">
                      <div className="cap-val cap-row-actions">
                        {canRemove && (
                          <Button
                            type="button"
                            variant="ghost"
                            style={{ paddingInline: 12 }}
                            disabled={clear.isPending}
                            aria-label={`Remove the special day on ${dayLabel(d.service_date)}`}
                            onClick={() => {
                              if (!locationId) return noLocation();
                              setRemoving(d.service_date);
                            }}
                          >
                            Remove
                          </Button>
                        )}
                        <HistoryLink
                          entityType="service_date_overrides"
                          entityId={d.id}
                          name={`the special day on ${dayLabel(d.service_date)}`}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <ConfirmDelete
        open={closing !== null}
        onClose={() => setClosing(null)}
        permission="schedule.manage"
        title={closing ? `Close ${dayLabel(closing.day)}?` : ''}
        description={`Customers won't be able to pick this day.${closing?.note ? ` Saved with the note “${closing.note}”.` : ''} To open it again, remove it from Upcoming special days.`}
        consequencesTitle="Already booked:"
        consequences={
          closingBooked
            ? [
                `${pizzas(closingBooked)} already booked that day stay booked. Let those customers know if you can't make them.`,
              ]
            : undefined
        }
        actionLabel="Close this day"
        pendingLabel="Closing…"
        onConfirm={async () => {
          if (!closing || !locationId) throw new Error('No location');
          await override.mutateAsync({
            location_id: locationId,
            service_date: closing.day,
            is_closed: true,
            ...(closing.note ? { note: closing.note } : {}),
          });
        }}
      />
      <ConfirmDelete
        open={removing !== null}
        onClose={() => setRemoving(null)}
        permission={null}
        title={removing ? `Remove the special day on ${dayLabel(removing)}?` : ''}
        description={
          removing
            ? `${removingSetTo ? `It is set to ${removingSetTo}. ` : ''}Your regular weekly hours apply to that day again. Orders already booked are kept.`
            : undefined
        }
        askReason
        actionLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={async ({ reason }) => {
          if (!removing || !locationId) throw new Error('No location');
          await clear.mutateAsync({ locId: locationId, day: removing, reason });
        }}
      />
    </Section>
  );
}
